import {
  AfterViewInit,
  Directive,
  ElementRef,
  HostBinding,
  Input,
  NgZone,
  OnDestroy,
  Renderer2,
} from '@angular/core';

@Directive({
  selector: 'th[appResizableColumn]'
})
export class ResizableColumnDirective implements AfterViewInit, OnDestroy {
  /** Índice (base 10) de la columna dentro de la fila de <thead> */
  @Input() columnIndex!: number;

  /** Clave para persistir ancho en localStorage (opcional) */
  @Input() storageKey?: string;

  /** Anchos mínimo y máximo (px) */
  @Input() minWidth = 60;
  @Input() maxWidth = 8000;

  @HostBinding('style.position') position = 'relative';

  private tableEl!: HTMLTableElement | null;
  private handleEl!: HTMLElement;

  private startX = 0;
  private startWidth = 0;

  // Unlisteners globales para limpiar eventos
  private mouseMoveUnlisten?: () => void;
  private mouseUpUnlisten?: () => void;
  private touchMoveUnlisten?: () => void;
  private touchEndUnlisten?: () => void;

  constructor(
    private el: ElementRef<HTMLElement>,
    private renderer: Renderer2,
    private zone: NgZone
  ) { }

  ngAfterViewInit(): void {
    this.tableEl = this.el.nativeElement.closest('table');

    // Crea el "asa" (handle) para redimensionar
    this.handleEl = this.renderer.createElement('span');
    this.renderer.addClass(this.handleEl, 'resize-handle');
    this.renderer.appendChild(this.el.nativeElement, this.handleEl);

    // Icono SVG inline (elegante, liviano)
    this.handleEl.innerHTML = `
  <svg class="resize-grip" width="14" height="14" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <!-- Dos columnas de puntos verticales (grip) -->
    <circle cx="8"  cy="6" r="1.5"></circle>
    <circle cx="8"  cy="12" r="1.5"></circle>
    <circle cx="8"  cy="18" r="1.5"></circle>
    <circle cx="16" cy="6" r="1.5"></circle>
    <circle cx="16" cy="12" r="1.5"></circle>
    <circle cx="16" cy="18" r="1.5"></circle>
  </svg>
`;

    // Opcional: etiqueta accesible para lectores de pantalla
    this.handleEl.setAttribute('role', 'separator');
    this.handleEl.setAttribute('aria-orientation', 'vertical');
    this.handleEl.setAttribute('aria-label', 'Adjust column width');

    // Restaura ancho persistido
    const defaultHeaderSize = '250';
    let persisted = this.storageKey ? localStorage.getItem(this.storageKey):defaultHeaderSize;
    persisted = persisted ? persisted :defaultHeaderSize;
    
    if (persisted) {
      const w = parseInt(persisted, 10);
      if (!Number.isNaN(w)) this.applyWidth(w);
    }

    // Suscribir handlers separados para mouse y touch
    this.zone.runOutsideAngular(() => {
      this.renderer.listen(this.handleEl, 'mousedown', (e: MouseEvent) => this.onMouseDown(e));
      this.renderer.listen(this.handleEl, 'touchstart', (e: TouchEvent) => this.onTouchStart(e));
    });
  }

  ngOnDestroy(): void {
    this.detachGlobalListeners();
  }

  // =========================
  // Handlers de MOUSE
  // =========================
  private onMouseDown(e: MouseEvent) {
    e.preventDefault();
    this.beginResize(e.clientX);
    this.zone.runOutsideAngular(() => {
      this.mouseMoveUnlisten = this.renderer.listen('document', 'mousemove', (ev: MouseEvent) => this.onMouseMove(ev));
      this.mouseUpUnlisten = this.renderer.listen('document', 'mouseup', () => this.onMouseUp());
    });
  }

  private onMouseMove(e: MouseEvent) {
    this.updateWidthFromPointer(e.clientX);
  }

  private onMouseUp() {
    this.endResize();
  }

  // =========================
  // Handlers de TOUCH
  // =========================
  private onTouchStart(e: TouchEvent) {
    if (e.touches.length === 0) return;
    e.preventDefault();
    const x = e.touches[0].clientX;
    this.beginResize(x);

    this.zone.runOutsideAngular(() => {
      this.touchMoveUnlisten = this.renderer.listen('document', 'touchmove', (ev: TouchEvent) => this.onTouchMove(ev));
      this.touchEndUnlisten = this.renderer.listen('document', 'touchend', () => this.onTouchEnd());
    });
  }

  private onTouchMove(e: TouchEvent) {
    if (e.touches.length === 0) return;
    this.updateWidthFromPointer(e.touches[0].clientX);
  }

  private onTouchEnd() {
    this.endResize();
  }

  // =========================
  // Lógica común
  // =========================
  private beginResize(clientX: number) {
    const th = this.el.nativeElement as HTMLElement;
    const rect = th.getBoundingClientRect();
    this.startX = clientX;
    this.startWidth = rect.width;
    this.renderer.addClass(document.body, 'resizing-col');
  }

  private updateWidthFromPointer(currentClientX: number) {
    let newWidth = Math.round(this.startWidth + (currentClientX - this.startX));
    newWidth = Math.max(this.minWidth, Math.min(this.maxWidth, newWidth));
    this.applyWidth(newWidth, /*persist=*/false);
  }

  private endResize() {
    this.detachGlobalListeners();
    this.renderer.removeClass(document.body, 'resizing-col');

    // Persiste ancho final
    if (this.storageKey) {
      const th = this.el.nativeElement as HTMLElement;
      const w = Math.round(th.getBoundingClientRect().width);
      localStorage.setItem(this.storageKey, String(w));
    }
  }

  private detachGlobalListeners() {
    this.mouseMoveUnlisten?.(); this.mouseMoveUnlisten = undefined;
    this.mouseUpUnlisten?.(); this.mouseUpUnlisten = undefined;
    this.touchMoveUnlisten?.(); this.touchMoveUnlisten = undefined;
    this.touchEndUnlisten?.(); this.touchEndUnlisten = undefined;
  }

  /** Aplica ancho a <th> y a las celdas <td> de la misma columna */
  private applyWidth(widthPx: number, persist = false) {
    const th = this.el.nativeElement as HTMLElement;

    // Asegura que la tabla exista (puede ser null si aún no está en DOM)
    if (!this.tableEl) {
      this.tableEl = th.closest('table');
    }

    // Fijar en header
    this.renderer.setStyle(th, 'width', `${widthPx}px`);
    this.renderer.setStyle(th, 'minWidth', `${widthPx}px`);
    this.renderer.setStyle(th, 'maxWidth', `${widthPx}px`);
/* not required th control row size
    // Fijar en body (todas las filas)
    const tbody = this.tableEl?.tBodies?.[0];
    if (tbody) {
      const rows = Array.from(tbody.rows);
      for (const row of rows) {
        const cell = row.cells?.[this.columnIndex] as HTMLElement | undefined;
        if (!cell) continue;
        this.renderer.setStyle(cell, 'width', `${widthPx}px`);
        this.renderer.setStyle(cell, 'minWidth', `${widthPx}px`);
        this.renderer.setStyle(cell, 'maxWidth', `${widthPx}px`);
      }
    }
*/
    if (persist && this.storageKey) {
      localStorage.setItem(this.storageKey, String(widthPx));
    }
  }
}
